import { DEFAULT_MODE, HOSTS } from 'core'
import { type ReactNode, useRef } from 'react'
import {
  data,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from 'react-router'
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { isPrefetch } from 'remix-utils/is-prefetch'
import { MAIN_HOST, SPRITE } from '#constants.ts'
import { useNonce } from '#contexts/nonceProvider.tsx'
import { useIsSpriteCached } from '#hooks/useIsCached.tsx'
import { useUmami } from '#hooks/useUmami.tsx'
import styles from '#styles/tailwind.css?url'
import { ErrorBody } from '#ui/Errors/index.tsx'
import { WebsiteErrWrap } from '#ui/Errors/WebsiteErrWrap.tsx'
import { SkipLink } from '#ui/SkipLink.tsx'
import { getSessionUser } from '#utils/auth.server.ts'
import { csrf } from '#utils/csrf.server.ts'
import { getEnv } from '#utils/env.server.ts'
import { honeypot } from '#utils/honeypot.server.ts'
import { getCanonical } from '#utils/misc.ts'
import type { Route } from './+types/root.ts'

export type RootLoader = typeof loader

export const meta: Route.MetaFunction = ({ matches }) => {
  const description =
    'Automatically monitor your site’s performance and intercept issues as they emerge.'

  return [
    {
      title:
        'pagespeedlog.com | Website and app performance analytics tool with a memory',
    },
    { name: 'description', content: description },
    { property: 'og:title', content: description },
    {
      'script:ld+json': {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'pagespeedlog.com',
        url: MAIN_HOST,
      },
    },
    getCanonical(matches),
  ]
}

export const links: Route.LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: styles },
    // Favicon for modern browsers
    { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
    // Icon for iPhone or iPad home screens
    { rel: 'apple-touch-icon', href: '/favicons/icon-512.png' },
    {
      rel: 'alternate icon',
      type: 'image/png',
      href: '/favicons/favicon-32.png',
    },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const,
    { rel: 'preconnect', href: HOSTS.tech.live },
    { rel: 'preconnect', href: HOSTS.img.live },
    { rel: 'preconnect', href: HOSTS.img2.live },
  ]
}

let previousToken: readonly [string, string | null] = ['', null]

export async function loader({ request }: Route.LoaderArgs) {
  const user = await getSessionUser(request)

  // This is a bit annoying but prefetch requests should not set the csrf token
  // or it will be out of sync. Instead it should use the previous token
  const [csrfToken, csrfCookie] =
    isPrefetch(request) && previousToken[0]
      ? previousToken
      : await csrf.commitToken()
  if (!isPrefetch(request)) previousToken = [csrfToken, csrfCookie]

  return data(
    {
      ...(user && {
        user: {
          id: user?.id,
          mode: user?.mode,
          newUser: user?.newUser,
          role: user?.roles?.[0]?.name,
          tz: user?.tz,
        },
      }),
      env: getEnv(),
      csrfToken,
      honeyProps: await honeypot.getInputProps(),
    },
    { headers: { ...(csrfCookie ? { 'Set-Cookie': csrfCookie } : undefined) } }
  )
}

export const headers: Route.HeadersFunction = ({ loaderHeaders }) => {
  return loaderHeaders
}

export function Layout({ children }: { children: ReactNode }) {
  const data = useLoaderData<typeof loader>()
  const umamiRef = useRef<HTMLScriptElement>(null)
  const nonce = useNonce()
  const isSpriteCached = useIsSpriteCached()
  useUmami(umamiRef)
  // Be defensive because these are not available when `ErrorBoundary`
  const env = data?.env
  const user = data?.user

  return (
    <html
      className={`h-full ${user?.mode || DEFAULT_MODE}`}
      dir='ltr'
      lang='en'
    >
      <head>
        <meta charSet='utf-8' />
        <meta content='width=device-width,initial-scale=1' name='viewport' />
        {isSpriteCached ? null : (
          <link
            as='image'
            href={MAIN_HOST + SPRITE}
            rel='preload'
            type='image/svg+xml'
          />
        )}
        {env?.ALLOW_INDEXING ? null : (
          // Some search engines don’t support `X-Robots-Tag` header
          <meta content='noindex, nofollow' name='robots' />
        )}
        {/* <script
          nonce={nonce}
          src='https://unpkg.com/react-scan/dist/auto.global.js'
        /> */}
        <Meta />
        <Links />
        {env?.UMAMI_ENABLED ? (
          <script
            async
            data-auto-track='false'
            data-website-id={env?.UMAMI_ID}
            nonce={nonce}
            ref={umamiRef}
            src={`${HOSTS.tech.live}/script.js`}
          />
        ) : null}
      </head>
      <body className='h-full bg-base-3 text-base-8'>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${env ? JSON.stringify(env) : {}}`,
          }}
          nonce={nonce}
        />
        <SkipLink />
        <div className='h-full'>
          {children}
          {/* Don't update scroll if search params change */}
          <ScrollRestoration
            getKey={location => location.pathname}
            nonce={nonce}
          />
          <Scripts nonce={nonce} />
        </div>
      </body>
    </html>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  return (
    <AuthenticityTokenProvider token={loaderData.csrfToken}>
      <HoneypotProvider {...loaderData.honeyProps}>
        <Outlet />
      </HoneypotProvider>
    </AuthenticityTokenProvider>
  )
}

export function ErrorBoundary() {
  const error = useRouteError() as Error
  // const { pathname } = useLocation()

  // Render different error comp if the user is using the app vs website
  // TODO: see if removing this will reduce unused code
  // if (pathname.startsWith('/app')) return <AppRootError error={error} />
  return (
    <WebsiteErrWrap>
      <ErrorBody error={error} />
    </WebsiteErrWrap>
  )
}
